import { css } from 'styled-components';
import type { HeadingStyle } from 'generated/graphql';
import { HeadingLevel } from '../../heading/constants';
import {
  getHeadingFontSize,
  getHeadingTextTransform,
  getHeadingTextDecoration,
} from '../../heading/utils/styling';

type ReadonlyHeadingLikeParagraphProps = {
  children: React.ReactNode;
  headingStyles: ReadonlyArray<HeadingStyle>;
  fontFamily?: string;
};

/**
 * RP-6070 (M2b): the read-only twin of the editable `headingDecoration` leaf. The static
 * `Renderer` has no `decorate`/`renderLeaf` pipeline, so heading-like paragraphs are styled
 * here — with the user's H1 reporter styles (same helpers as the editable path) plus
 * `role="heading"` / `aria-level={1}` so signed/read-only reports stay semantic.
 *
 * Styles the whole line: in the flat model a heading is a label-only paragraph, so styling
 * the paragraph == styling the label. (H1 style helpers move out of the heading plugin in M9.)
 *
 * Renders a `<div role="heading">` rather than `<p>`: the editable paragraph renderer uses a
 * `<div>` to allow nested UGC (picklists/bookmarks), and a `<p>` cannot legally contain block
 * content — so a `<p>` here risks invalid HTML. The role/aria-level preserve heading semantics.
 */
export const ReadonlyHeadingLikeParagraph = ({
  children,
  headingStyles,
  fontFamily,
}: ReadonlyHeadingLikeParagraphProps): React.ReactElement => {
  const styles = [...headingStyles];
  const fontSize = getHeadingFontSize(styles, HeadingLevel.H1);
  const textTransform = getHeadingTextTransform(styles, HeadingLevel.H1);
  const textDecoration = getHeadingTextDecoration(styles, HeadingLevel.H1);

  return (
    <div
      role="heading"
      aria-level={1}
      css={css`
        margin: 0;
        text-transform: ${textTransform};
        font-family: ${fontFamily};
        font-size: ${fontSize}px;
        font-weight: ${textDecoration?.isBold === true ? 'bold' : 'normal'};
        font-style: ${textDecoration?.isItalic === true ? 'italic' : 'normal'};
        text-decoration: ${textDecoration?.isUnderline === true ? 'underline' : 'none'};
      `}
    >
      {children}
    </div>
  );
};

ReadonlyHeadingLikeParagraph.displayName = 'ReadonlyHeadingLikeParagraph';
